import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Wallet, CreditCard, Gift, History, QrCode } from "lucide-react";
import { api, formatApiError, STATUS_LABELS } from "../lib/api";

const txLabels = {
  earn: "Points gagnés",
  bonus: "Bonus carte +20%",
  campaign: "Bonus campagne",
  manual_add: "Ajout par le restaurant",
  manual_remove: "Retrait par le restaurant",
  redeem: "Échange récompense",
  refund_removal: "Remboursement",
  birthday: "Cadeau d'anniversaire",
  first_reservation: "Bonus de bienvenue",
};

const redStatus = {
  en_attente: "text-yellow-400 border-yellow-400/30",
  validee: "text-[#009246] border-[#009246]/40",
  refusee: "text-[#ff6b81] border-[#C8102E]/40",
};

export const WalletRitas = () => {
  const [wallet, setWallet] = useState(null);
  const [amount, setAmount] = useState("");
  const [paying, setPaying] = useState(false);

  const load = useCallback(() => {
    api.get("/wallet").then((r) => setWallet(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const pay = async (e) => {
    e.preventDefault();
    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout", { amount: Number(amount), origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setPaying(false);
    }
  };

  const redeem = async (rewardId) => {
    try {
      const { data } = await api.post("/wallet/redeem", { reward_id: rewardId });
      toast.success(`Récompense demandée ! Code : ${data.code}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (!wallet) return <p className="text-gray-500">Chargement du wallet…</p>;

  const progress = wallet.next_reward ? Math.min(100, Math.round((wallet.balance / wallet.next_reward.threshold) * 100)) : 100;

  return (
    <div className="space-y-8" data-testid="wallet-ritas">
      <div className="ic-card !transform-none p-8 relative overflow-hidden">
        <Wallet size={120} className="absolute -right-6 -bottom-6 text-[#D4AF37]/10" />
        <p className="gold-label mb-2">Mon Wallet RITAS</p>
        <p className="text-5xl font-semibold" data-testid="wallet-balance">{wallet.balance} <span className="text-xl text-[#D4AF37]">RITAS</span></p>
        {wallet.next_reward ? (
          <div className="mt-6 max-w-md">
            <div className="flex justify-between text-xs text-gray-400 mb-2">
              <span>Prochaine récompense : {wallet.next_reward.title}</span>
              <span>{wallet.balance} / {wallet.next_reward.threshold}</span>
            </div>
            <div className="h-2 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-[#D4AF37] rounded-full transition-transform duration-500" style={{ width: `${progress}%` }} data-testid="wallet-progress-bar" />
            </div>
          </div>
        ) : (
          <p className="text-sm text-[#009246] mt-4">Toutes les récompenses sont débloquées !</p>
        )}
        <p className="text-xs text-gray-500 mt-4">1 € payé = 1 RITAS · Paiement par carte = +20% bonus</p>
      </div>

      <form onSubmit={pay} className="ic-card !transform-none p-6" data-testid="wallet-pay-form">
        <p className="font-serif-elegant text-xl font-semibold mb-1 flex items-center gap-2"><CreditCard size={18} className="text-[#D4AF37]" /> Payer mon addition en ligne</p>
        <p className="text-xs text-gray-400 mb-4">Payez par carte et gagnez automatiquement vos RITAS avec 20% de bonus.</p>
        <div className="flex gap-3">
          <input data-testid="wallet-pay-amount" required type="number" min="1" max="1000" step="0.01" className="ic-input !w-40" placeholder="Montant €" value={amount} onChange={(e) => setAmount(e.target.value)} />
          <button type="submit" disabled={paying} data-testid="wallet-pay-btn" className="btn-gold !py-2.5 text-sm disabled:opacity-50">
            {paying ? "Redirection…" : "Payer par carte"}
          </button>
        </div>
      </form>

      <div>
        <p className="font-serif-elegant text-xl font-semibold mb-4 flex items-center gap-2"><Gift size={18} className="text-[#D4AF37]" /> Récompenses</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {wallet.rewards.map((r) => (
            <div key={r.id} className={`ic-card !transform-none p-5 ${!r.unlocked ? "opacity-60" : ""}`} data-testid={`reward-card-${r.id}`}>
              <div className="flex items-center justify-between">
                <p className="font-medium">{r.title}</p>
                <span className="text-[#D4AF37] text-sm font-medium">{r.threshold} RITAS</span>
              </div>
              <p className="text-xs text-gray-400 mt-1">{r.description}</p>
              <button
                data-testid={`reward-redeem-${r.id}`}
                disabled={!r.unlocked}
                onClick={() => redeem(r.id)}
                className={`mt-4 text-sm rounded-full px-5 py-2 ${r.unlocked ? "bg-[#D4AF37] text-black font-medium hover:bg-[#F3D568]" : "border border-white/15 text-gray-500 cursor-not-allowed"}`}
              >
                {r.unlocked ? "Échanger" : "Pas encore débloqué"}
              </button>
            </div>
          ))}
        </div>
      </div>

      {wallet.redemptions.length > 0 && (
        <div>
          <p className="font-serif-elegant text-xl font-semibold mb-4 flex items-center gap-2"><QrCode size={18} className="text-[#D4AF37]" /> Mes récompenses à utiliser</p>
          <div className="space-y-3">
            {wallet.redemptions.map((r) => (
              <div key={r.id} className="ic-card !transform-none p-5 flex flex-wrap items-center gap-5" data-testid={`redemption-${r.id}`}>
                {r.status === "en_attente" && (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/qrcode?data=${encodeURIComponent(r.code)}`} alt={r.code} className="w-20 h-20 rounded-lg bg-white p-1" />
                )}
                <div className="flex-1">
                  <p className="font-medium">{r.reward_title}</p>
                  <p className="text-sm text-[#D4AF37] font-mono mt-1">{r.code}</p>
                  <p className="text-xs text-gray-500 mt-1">Présentez ce code au restaurant · {r.cost} RITAS</p>
                </div>
                <span className={`text-xs border rounded-full px-3 py-1 ${redStatus[r.status]}`}>
                  {r.status === "en_attente" ? "À utiliser" : r.status === "validee" ? "Utilisée" : "Refusée (RITAS remboursés)"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <p className="font-serif-elegant text-xl font-semibold mb-4 flex items-center gap-2"><History size={18} className="text-[#D4AF37]" /> Historique RITAS</p>
        <div className="space-y-2">
          {wallet.transactions.length === 0 && <p className="text-sm text-gray-500">Aucune transaction pour le moment. Payez par carte pour gagner vos premiers RITAS !</p>}
          {wallet.transactions.map((t) => (
            <div key={t.id} className="ic-card !transform-none p-4 flex items-center justify-between text-sm">
              <div>
                <p className="text-gray-200">{t.description || txLabels[t.type]}</p>
                <p className="text-xs text-gray-500 mt-0.5">{new Date(t.created_at).toLocaleDateString("fr-FR")} · {txLabels[t.type] || t.type}</p>
              </div>
              <span className={`font-medium ${t.amount >= 0 ? "text-[#009246]" : "text-[#ff6b81]"}`}>{t.amount >= 0 ? "+" : ""}{t.amount}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
